import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CAlert,
  CButton,
  CCol,
  CInput,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRow,
  CSelect,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { getItem, postItem } from "../../api/generics";
import {
  OperationalActionCommand,
  OperationalDocument,
  OperationalList,
} from "../../models/operational-monitoring";
import { SUCCESS } from "../../utils/constants/tags";

interface Props {
  show: boolean;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

const statusLabels: Record<string, string> = {
  uploaded_to_set: "Subido, sin aprobación",
  error: "Error",
  recoverable_error: "Error recuperable",
  set_unknown_error: "Error desconocido",
};

const actionLabels: Record<string, string> = {
  consult: "Consultar por CDC",
  retry: "Reintentar envío",
  regenerate_cdc: "Invalidar CDC y regenerar",
};

const commandStatusLabels: Record<string, string> = {
  queued: "En cola",
  running: "En ejecución",
  completed: "Completado",
  completed_with_errors: "Completado con errores",
  failed: "Falló",
};

const activeCommandStatuses = new Set(["queued", "running"]);

const normalize = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const OperationalDocumentsModal: React.FC<Props> = ({
  show,
  onClose,
  onChanged,
}) => {
  const [documents, setDocuments] = useState<OperationalDocument[]>([]);
  const [commands, setCommands] = useState<OperationalActionCommand[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{
    color: "success" | "danger" | "warning";
    text: string;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [responseCode, setResponseCode] = useState("");
  const [action, setAction] = useState("consult");

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    const response = await getItem<OperationalList<OperationalDocument>>(
      "/admin-stats/operations/documents/",
    );
    if (response.status === SUCCESS && response.data) {
      setDocuments(response.data.items);
      const availableKeys = new Set(response.data.items.map((item) => item.key));
      setSelected(
        (current) => new Set(Array.from(current).filter((key) => availableKeys.has(key))),
      );
    } else {
      setMessage({
        color: "danger",
        text: response.detail ?? "No se pudieron cargar los documentos.",
      });
    }
    setLoading(false);
  }, []);

  const loadCommands = useCallback(async (): Promise<boolean> => {
    const response = await getItem<OperationalList<OperationalActionCommand>>(
      "/admin-stats/operations/documents/actions/",
    );
    if (response.status === SUCCESS && response.data) {
      setCommands(response.data.items);
      return response.data.items.some((command) =>
        activeCommandStatuses.has(command.status),
      );
    }
    return false;
  }, []);

  useEffect(() => {
    if (show) {
      setMessage(null);
      loadDocuments();
      loadCommands();
    }
  }, [show, loadCommands, loadDocuments]);

  const hasActiveCommands = commands.some((command) =>
    activeCommandStatuses.has(command.status),
  );

  useEffect(() => {
    if (!show || !hasActiveCommands) return;

    const timer = window.setInterval(async () => {
      const stillActive = await loadCommands();
      if (!stillActive) {
        await loadDocuments();
        await onChanged();
      }
    }, 2000);

    return () => window.clearInterval(timer);
  }, [hasActiveCommands, loadCommands, loadDocuments, onChanged, show]);

  const filteredDocuments = useMemo(() => {
    const needle = normalize(search);
    const errorCode = normalize(responseCode);
    return documents.filter((document) => {
      if (type && document.type !== type) return false;
      if (status && document.status !== status) return false;
      if (errorCode && !normalize(document.responseCode).includes(errorCode)) {
        return false;
      }
      if (!needle) return true;
      return normalize(
        [
          document.cdc,
          document.documentNumber,
          document.taxPayerRuc,
          document.taxPayerName,
          document.responseCode,
          document.responseDescription,
          document.error,
        ].join(" "),
      ).includes(needle);
    });
  }, [documents, responseCode, search, status, type]);

  const allFilteredSelected =
    filteredDocuments.length > 0 &&
    filteredDocuments.every((document) => selected.has(document.key));

  const toggleAll = () => {
    setSelected((current) => {
      const next = new Set(current);
      for (const document of filteredDocuments) {
        if (allFilteredSelected) next.delete(document.key);
        else next.add(document.key);
      }
      return next;
    });
  };

  const toggleDocument = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const executeAction = async () => {
    const selectedDocuments = documents.filter((document) => selected.has(document.key));
    if (selectedDocuments.length === 0) return;

    if (
      action === "regenerate_cdc" &&
      !window.confirm(
        `Se invalidará el CDC actual de ${selectedDocuments.length} documento(s) y se generará uno nuevo. ¿Desea continuar?`,
      )
    ) {
      return;
    }

    setSubmitting(true);
    setMessage(null);
    const response = await postItem<OperationalActionCommand>(
      "/admin-stats/operations/documents/actions/",
      {
        action,
        documents: selectedDocuments.map(({ type: documentType, id }) => ({
          type: documentType,
          id,
        })),
      } as any,
    );

    if (response.status !== SUCCESS || !response.data) {
      setMessage({
        color: "danger",
        text: response.detail ?? "No se pudo ejecutar la acción.",
      });
    } else {
      setMessage({
        color: "success",
        text: `Comando #${response.data.id} encolado para ${response.data.totalCount} documento(s). El progreso aparecerá en el historial.`,
      });
      setSelected(new Set());
      await loadCommands();
    }
    setSubmitting(false);
  };

  return (
    <CModal show={show} onClose={onClose} size="xl">
      <CModalHeader closeButton>
        <CModalTitle>Documentos subidos sin aprobación y con error</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ maxHeight: "82vh", overflowY: "auto" }}>
        {message && <CAlert color={message.color}>{message.text}</CAlert>}

        <CRow className="mb-3 align-items-end">
          <CCol md="5">
            <label>Acción para seleccionados</label>
            <CSelect value={action} onChange={(event: any) => setAction(event.target.value)}>
              <option value="consult">Consultar por CDC</option>
              <option value="retry">Reintentar envío</option>
              <option value="regenerate_cdc">Invalidar CDC y regenerar</option>
            </CSelect>
          </CCol>
          <CCol md="3">
            <CButton
              color={action === "regenerate_cdc" ? "danger" : "primary"}
              onClick={executeAction}
              disabled={selected.size === 0 || submitting}
              block
            >
              {submitting ? "Procesando…" : `Ejecutar (${selected.size})`}
            </CButton>
          </CCol>
          <CCol md="4" className="text-md-right mt-2 mt-md-0">
            <CButton
              color="secondary"
              variant="outline"
              onClick={toggleAll}
              disabled={filteredDocuments.length === 0}
            >
              {allFilteredSelected
                ? "Desmarcar filtrados"
                : `Marcar filtrados (${filteredDocuments.length})`}
            </CButton>
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md="4">
            <CInput
              value={search}
              onChange={(event: any) => setSearch(event.target.value)}
              placeholder="CDC, número, contribuyente o error"
              aria-label="Buscar documentos"
            />
          </CCol>
          <CCol md="3">
            <CSelect value={type} onChange={(event: any) => setType(event.target.value)}>
              <option value="">Todos los tipos</option>
              <option value="invoices">Facturas</option>
              <option value="credit_notes">Notas de crédito</option>
              <option value="debit_notes">Notas de débito</option>
              <option value="remission_notes">Notas de remisión</option>
              <option value="auto_invoices">Autofacturas</option>
            </CSelect>
          </CCol>
          <CCol md="3">
            <CSelect value={status} onChange={(event: any) => setStatus(event.target.value)}>
              <option value="">Todos los estados</option>
              <option value="uploaded_to_set">Subidos sin aprobación</option>
              <option value="error">Error</option>
              <option value="recoverable_error">Error recuperable</option>
              <option value="set_unknown_error">Error desconocido</option>
            </CSelect>
          </CCol>
          <CCol md="2">
            <CInput
              value={responseCode}
              onChange={(event: any) => setResponseCode(event.target.value)}
              placeholder="Código error"
              aria-label="Filtrar código de error"
            />
          </CCol>
        </CRow>

        <div className="small text-muted mb-2">
          {filteredDocuments.length} visible(s) de {documents.length}; {selected.size} seleccionado(s).
          Los filtros no cambian la selección.
        </div>

        <div className="table-responsive" style={{ minHeight: 180 }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <table className="table table-sm table-hover table-bordered">
              <thead className="thead-light">
                <tr>
                  <th className="text-center" style={{ width: 38 }}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAll}
                      aria-label="Seleccionar todos los documentos filtrados"
                    />
                  </th>
                  <th>Tipo / número</th>
                  <th>Contribuyente</th>
                  <th>CDC</th>
                  <th>Estado</th>
                  <th>Error SIFEN</th>
                  <th>Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((document) => (
                  <tr key={document.key}>
                    <td className="text-center align-middle">
                      <input
                        type="checkbox"
                        checked={selected.has(document.key)}
                        onChange={() => toggleDocument(document.key)}
                        aria-label={`Seleccionar ${document.cdc}`}
                      />
                    </td>
                    <td>
                      <div>{document.typeLabel}</div>
                      <span className="text-muted">{document.documentNumber || "-"}</span>
                    </td>
                    <td>
                      <div>{document.taxPayerName}</div>
                      <span className="text-muted">{document.taxPayerRuc}</span>
                    </td>
                    <td style={{ minWidth: 230, wordBreak: "break-all" }}>{document.cdc}</td>
                    <td>{statusLabels[document.status] ?? document.status}</td>
                    <td style={{ minWidth: 210 }}>
                      <strong>{document.responseCode || "-"}</strong>
                      <div>{document.responseDescription || document.error || "-"}</div>
                    </td>
                    <td>{new Date(document.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
                {filteredDocuments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No hay documentos para los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <hr />
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Historial de comandos</h5>
          <CButton color="secondary" variant="outline" size="sm" onClick={loadCommands}>
            Actualizar historial
          </CButton>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="thead-light">
              <tr>
                <th>Comando</th>
                <th>Acción</th>
                <th>Solicitado por</th>
                <th>Estado / progreso</th>
                <th>Resultado</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {commands.map((command) => {
                const failures = (command.items ?? []).filter(
                  (item) => item.status === "failed",
                );
                return (
                  <tr key={command.id}>
                    <td>#{command.id}</td>
                    <td>{actionLabels[command.action] ?? command.action}</td>
                    <td>{command.requestedByUsername}</td>
                    <td style={{ minWidth: 180 }}>
                      <div>{commandStatusLabels[command.status] ?? command.status}</div>
                      <span className="text-muted">
                        {command.processedCount}/{command.totalCount}
                      </span>
                    </td>
                    <td style={{ minWidth: 260 }}>
                      <div>
                        {command.succeededCount} correcto(s), {command.failedCount} con error
                      </div>
                      {command.error && <div className="text-danger">{command.error}</div>}
                      {failures.slice(0, 2).map((item) => (
                        <div className="text-danger small" key={item.id}>
                          {item.documentType} #{item.documentId}: {item.detail}
                        </div>
                      ))}
                      {failures.length > 2 && (
                        <div className="text-muted small">
                          y {failures.length - 2} error(es) más
                        </div>
                      )}
                    </td>
                    <td>{new Date(command.createdAt).toLocaleString()}</td>
                  </tr>
                );
              })}
              {commands.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    No hay comandos ejecutados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CModalBody>
    </CModal>
  );
};

export default OperationalDocumentsModal;
